import { FRAME, MAX_CLIP_DURATION_SECONDS, type ClipWindow } from '@/features/coach-review/clipTrack';

/** The clip a Moment → Clip switch seeds (F3) and `I`/`O` restart at. */
export const CLIP_SEED_SECONDS = 10;
const MIN_CLIP = FRAME * 5;
export const CLIP_PRESETS = [5, 10, 20, 30] as const;

/** Hard stop at 120 s: the handle being moved stops at the limit instead of
 * dragging the other one along. Never shorter than five frames or past the end. */
export function boundClipWindow(next:ClipWindow, previous:ClipWindow, duration:number):ClipWindow {
  const end = duration > 0 ? duration : Number.POSITIVE_INFINITY;
  let start = Math.max(0, Math.min(next.start, end)), stop = Math.max(0, Math.min(next.end, end));
  const startMoved = start !== previous.start && stop === previous.end;
  if (stop - start > MAX_CLIP_DURATION_SECONDS) { if (startMoved) start = stop - MAX_CLIP_DURATION_SECONDS; else stop = start + MAX_CLIP_DURATION_SECONDS; }
  if (stop - start < MIN_CLIP) {
    // Grow the moved side first; at either end of the recording, grow the other.
    if (startMoved) start = Math.max(0, stop - MIN_CLIP); else stop = Math.min(end, start + MIN_CLIP);
    if (stop - start < MIN_CLIP) { if (startMoved) stop = Math.min(end, start + MIN_CLIP); else start = Math.max(0, stop - MIN_CLIP); }
  }
  return { start, end:stop };
}
/** A new clip from the playhead (F3): in-point at the playhead, out-point the
 * default length later, capped at a known recording's end (and never shorter
 * than five frames, so the in-point moves back only at the very end). */
export function seedClipWindow(at:number, duration:number):ClipWindow {
  if (!(duration > 0)) return { start:at, end:at + CLIP_SEED_SECONDS };
  const start = Math.max(0, Math.min(at, duration - MIN_CLIP));
  return boundClipWindow({ start, end:start + CLIP_SEED_SECONDS }, { start:-1, end:-1 }, duration);
}
/** `I` at the playhead. A range that would end up empty or past 120 s restarts at the default length. */
export function clipInAt(window:ClipWindow, at:number, duration:number):ClipWindow {
  const keeps = window.end - at >= MIN_CLIP && window.end - at <= MAX_CLIP_DURATION_SECONDS;
  return boundClipWindow({ start:at, end:keeps ? window.end : at + CLIP_SEED_SECONDS }, { start:-1, end:-1 }, duration);
}
/** `O` at the playhead. Before the in-point it moves the in-point back instead of inverting the range. */
export function clipOutAt(window:ClipWindow, at:number, duration:number):ClipWindow {
  let start = window.start;
  if (at - start < MIN_CLIP) start = Math.max(0, at - CLIP_SEED_SECONDS);
  if (at - start > MAX_CLIP_DURATION_SECONDS) start = at - MAX_CLIP_DURATION_SECONDS;
  return boundClipWindow({ start, end:at }, { start:-1, end:-1 }, duration);
}
/** Preset lengths from the in-point, capped at the recording's end. */
export function clipPreset(window:ClipWindow, seconds:number, duration:number):ClipWindow {
  return boundClipWindow({ start:window.start, end:window.start + seconds }, { start:window.start, end:-1 }, duration);
}
